import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Brush, CartesianGrid, Label, Line, LineChart, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';

import { Settings } from '@mui/icons-material';
import PlotSettings from '../PlotSettings';
import PlotStats from '../PlotStats';
import MetRateTooltip from '../MetRate/MetRateTooltip';
import Timer from '../Timer';
import config from '../../../config';
import { formatTimestampHHMMSS } from '../../../helpers/datetime';

const ev1Stroke = '#BA0000';
const ev2Stroke = '#FE8484';

const measure = 'hr';
const unit = 'bpm';

const CHART_HEIGHT = 400;
const CHART_WIDTH = 660;
const COMPACT_HEIGHT = 280;

/** Red/green vs current HR, or neutral when latest HR is missing. */
function safetyReferenceStroke(safetyBpm, lastHr) {
  if (lastHr == null || lastHr === '') {
    return '#757575';
  }
  return safetyBpm < lastHr ? 'red' : 'green';
}

export default function HeartRate({
  data = [],
  minHrBPM,
  maxHrBPM,
  hideSettings = false,
  compact = false,
}) {
  const [yMin, setYMin] = useState(minHrBPM || 50);
  const [yMax, setYMax] = useState(maxHrBPM || 200);
  const [btuTickCount, setBTUTickCount] = useState(15);

  const [chartWindowMinNum, setChartWindowMinNum] = useState(0);

  /** null = use chart window (rolling) or full range; number = explicit brush range */
  const [brushStart, setBrushStart] = useState(null);
  const [brushEnd, setBrushEnd] = useState(null);

  const [settingsOpen, setSettingsOpen] = useState(false);

  const [ev1Active, setEV1Active] = useState(true);
  const [ev2Active, setEV2Active] = useState(true);

  const [safetyHR, setSafetyHR] = useState({
    EV1: 150,
    EV2: 150,
  });

  const [hrCountdown, setHrCountdown] = useState({
    EV1: null,
    EV2: null,
  });

  const closeSettings = () => setSettingsOpen(false);

  const handleBrushUpdate = ({
    startIndex,
    endIndex,
  }) => {
    setBrushStart(startIndex);
    setBrushEnd(((data.length - 1) - endIndex <= 1) ? null : endIndex);
  };

  const handleSafetyHRChange = (event, ev) => {
    const next = parseInt(event.target.value, config.PARSE_INT_BASE);
    setSafetyHR((prev) => ({
      ...prev,
      [ev]: Number.isNaN(next) ? prev[ev] : next,
    }));
  };

  useEffect(() => {
    setBrushStart(null);
    setBrushEnd(null);
  }, [chartWindowMinNum]);

  const togglePlotSettings = () => setSettingsOpen(!settingsOpen);

  const last = data.at(-1);

  useEffect(() => {
    setHrCountdown((prev) => {
      const next = { ...prev };
      if (last?.EV1hr > safetyHR.EV1 && !prev.EV1) next.EV1 = Date.now();
      if (last?.EV1hr != null && last.EV1hr <= safetyHR.EV1 && prev.EV1) next.EV1 = null;
      if (last?.EV2hr > safetyHR.EV2 && !prev.EV2) next.EV2 = Date.now();
      if (last?.EV2hr != null && last.EV2hr <= safetyHR.EV2 && prev.EV2) next.EV2 = null;
      return next;
    });
  }, [data, safetyHR]);

  const dataLength = data.length;
  const brushDefaultStart = chartWindowMinNum && dataLength
    ? Math.max(dataLength - 60 * chartWindowMinNum, 0)
    : 0;
  const brushDefaultEnd = dataLength > 0 ? dataLength - 1 : 0;
  const brushStartIndex = brushStart ?? brushDefaultStart;
  const brushEndIndex = brushEnd ?? brushDefaultEnd;

  const hasData = data.length > 0;
  /** Websocket points use formattedTimestamp; some fixtures use timestamp. */
  const xAxisDataKey = useMemo(() => {
    if (!data.length) return 'formattedTimestamp';
    return data.some((d) => d?.formattedTimestamp != null && d.formattedTimestamp !== '')
      ? 'formattedTimestamp'
      : 'timestamp';
  }, [data]);
  const displayTimestamp = last?.formattedTimestamp
    || (last?.timestamp != null ? formatTimestampHHMMSS(last.timestamp) : undefined);

  return (
    <div className={`heart-rate plot${compact ? ' compact' : ''}`}>
      <div className="header">
        <span>Heart Rate</span>
        {!hideSettings && (
        <div className="settings-controller">
          <Settings data-testid="heart-rate-plot-settings" onClick={togglePlotSettings} />
          {settingsOpen
            ? (
              <PlotSettings
                setYMin={setYMin}
                startYMin={yMin}
                setYMax={setYMax}
                startYMax={yMax}
                setTickCount={setBTUTickCount}
                startTickCount={btuTickCount}
                setChartWindow={setChartWindowMinNum}
                startChartWindow={chartWindowMinNum}
                setSafetyHR={handleSafetyHRChange}
                safetyHR={safetyHR}
              />
            ) : null}
        </div>
        )}
      </div>
      <div className="stats flex-row" onClick={closeSettings} role="presentation">
        <PlotStats
          color={ev1Stroke}
          data={last?.EV1hr}
          avg={last?.EV1avgHR}
          isActive={ev1Active}
          setActive={() => setEV1Active(!ev1Active)}
          timestamp={displayTimestamp}
          hasData={hasData}
          showAvg
          ev="EV1"
          unit={unit}
        />
        <PlotStats
          color={ev2Stroke}
          data={last?.EV2hr}
          avg={last?.EV2avgHr}
          isActive={ev2Active}
          setActive={() => setEV2Active(!ev2Active)}
          timestamp={displayTimestamp}
          hasData={hasData}
          showAvg
          ev="EV2"
          unit={unit}
        />

        {(ev1Active && safetyHR.EV1 && hrCountdown.EV1)
          ? <Timer start={hrCountdown.EV1} ev="EV1" /> : null}
        {(ev2Active && safetyHR.EV2 && hrCountdown.EV2)
          ? <Timer start={hrCountdown.EV2} ev="EV2" /> : null}
      </div>
      <span>{unit}</span>
      <div className="heart-rate-chart-container" onClick={closeSettings} role="presentation">
        {compact ? (
          <ResponsiveContainer width="100%" height={COMPACT_HEIGHT}>
            <LineChart data={data}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey={xAxisDataKey} interval="preserveStartEnd" />
              <YAxis
                yAxisId={measure}
                domain={[Math.max((yMin == null ? minHrBPM : yMin), 0), yMax || maxHrBPM]}
                interval={0}
                tickCount={btuTickCount}
                tickFormatter={Math.round}
                allowDataOverflow
                type="number"
              >
                <Label position="top" textAnchor="start" value={unit} />
              </YAxis>
              <Tooltip content={<MetRateTooltip />} />
              {ev1Active
                ? (
                  <Line
                    animationDuration={50}
                    animationEasing="linear"
                    dataKey={`EV1${measure}`}
                    dot={false}
                    isAnimationActive
                    key={`EV1${measure}`}
                    stroke={ev1Stroke}
                    strokeWidth={2}
                    type="monotone"
                    yAxisId={measure}
                  />
                ) : null}
              {ev2Active && data.length > 0
                ? (
                  <Line
                    animationDuration={50}
                    animationEasing="linear"
                    dataKey={`EV2${measure}`}
                    dot={false}
                    isAnimationActive
                    key={`EV2${measure}`}
                    stroke={ev2Stroke}
                    strokeWidth={2}
                    type="monotone"
                    yAxisId={measure}
                  />
                ) : null}
              {(data && data.length)
                ? (
                  <Brush
                    data={data}
                    dataKey={xAxisDataKey}
                    height={compact ? 24 : 40}
                    padding={{
                      left: 0,
                      right: 10,
                    }}
                    tick
                    startIndex={brushStartIndex}
                    endIndex={brushEndIndex}
                    onChange={handleBrushUpdate}
                    travellerWidth={6}
                  >
                    <LineChart data={data}>
                      <Line
                        animationDuration={50}
                        animationEasing="linear"
                        dataKey={`EV1${measure}`}
                        dot={false}
                        isAnimationActive
                        key="brush-ev1"
                        stroke={ev1Stroke}
                        type="monotone"
                      />
                      <Line
                        animationDuration={50}
                        animationEasing="linear"
                        dataKey={`EV2${measure}`}
                        dot={false}
                        isAnimationActive
                        key="brush-ev2"
                        stroke={ev2Stroke}
                        type="monotone"
                      />
                    </LineChart>
                  </Brush>
                ) : null}
              {(ev1Active && safetyHR.EV1 && hasData)
                ? (
                  <ReferenceLine
                    ifOverflow="extendDomain"
                    y={safetyHR.EV1}
                    stroke={safetyReferenceStroke(safetyHR.EV1, last?.EV1hr)}
                    yAxisId={measure}
                  >
                    <Label
                      value={`EV1 Safety HR — ${safetyHR.EV1} ${unit}`}
                      position="insideTopRight"
                    />
                  </ReferenceLine>
                ) : null}
              {(ev2Active && safetyHR.EV2 && hasData)
                ? (
                  <ReferenceLine
                    ifOverflow="extendDomain"
                    y={safetyHR.EV2}
                    stroke={safetyReferenceStroke(safetyHR.EV2, last?.EV2hr)}
                    yAxisId={measure}
                  >
                    <Label
                      value={`EV2 Safety HR — ${safetyHR.EV2} ${unit}`}
                      position={safetyHR.EV1 === safetyHR.EV2 ? 'insideBottomRight' : 'insideTopRight'}
                    />
                  </ReferenceLine>
                ) : null}
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <LineChart data={data} height={CHART_HEIGHT} width={CHART_WIDTH}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey={xAxisDataKey} interval="preserveStartEnd" />
            <YAxis
              yAxisId={measure}
              domain={[Math.max((yMin == null ? minHrBPM : yMin), 0), yMax || maxHrBPM]}
              interval={0}
              tickCount={btuTickCount}
              tickFormatter={Math.round}
              allowDataOverflow
              type="number"
            >
              <Label position="top" textAnchor="start" value={unit} />
            </YAxis>
            <Tooltip content={<MetRateTooltip />} />
            {ev1Active
              ? (
                <Line
                  animationDuration={50}
                  animationEasing="linear"
                  dataKey={`EV1${measure}`}
                  dot={false}
                  isAnimationActive
                  key={`EV1${measure}`}
                  stroke={ev1Stroke}
                  strokeWidth={2}
                  type="monotone"
                  yAxisId={measure}
                />
              ) : null}
            {ev2Active && data.length > 0
              ? (
                <Line
                  animationDuration={50}
                  animationEasing="linear"
                  dataKey={`EV2${measure}`}
                  dot={false}
                  isAnimationActive
                  key={`EV2${measure}`}
                  stroke={ev2Stroke}
                  strokeWidth={2}
                  type="monotone"
                  yAxisId={measure}
                />
              ) : null}
            {(data && data.length)
              ? (
                <Brush
                  data={data}
                  dataKey={xAxisDataKey}
                  height={40}
                  padding={{
                    left: 0,
                    right: 10,
                  }}
                  tick
                  startIndex={brushStartIndex}
                  endIndex={brushEndIndex}
                  onChange={handleBrushUpdate}
                  travellerWidth={6}
                >
                  <LineChart data={data}>
                    <Line
                      animationDuration={50}
                      animationEasing="linear"
                      dataKey={`EV1${measure}`}
                      dot={false}
                      isAnimationActive
                      key="brush-ev1-full"
                      stroke={ev1Stroke}
                      type="monotone"
                    />
                    <Line
                      animationDuration={50}
                      animationEasing="linear"
                      dataKey={`EV2${measure}`}
                      dot={false}
                      isAnimationActive
                      key="brush-ev2-full"
                      stroke={ev2Stroke}
                      type="monotone"
                    />
                  </LineChart>
                </Brush>
              ) : null}
            {(ev1Active && safetyHR.EV1 && hasData)
              ? (
                <ReferenceLine
                  ifOverflow="extendDomain"
                  y={safetyHR.EV1}
                  stroke={safetyReferenceStroke(safetyHR.EV1, last?.EV1hr)}
                  yAxisId={measure}
                >
                  <Label
                    value={`EV1 Safety HR — ${safetyHR.EV1} ${unit}`}
                    position="insideTopRight"
                  />
                </ReferenceLine>
              ) : null}
            {(ev2Active && safetyHR.EV2 && hasData)
              ? (
                <ReferenceLine
                  ifOverflow="extendDomain"
                  y={safetyHR.EV2}
                  stroke={safetyReferenceStroke(safetyHR.EV2, last?.EV2hr)}
                  yAxisId={measure}
                >
                  <Label
                    value={`EV2 Safety HR — ${safetyHR.EV2} ${unit}`}
                    position={safetyHR.EV1 === safetyHR.EV2 ? 'insideBottomRight' : 'insideTopRight'}
                  />
                </ReferenceLine>
              ) : null}
          </LineChart>
        )}
      </div>
    </div>
  );
}

HeartRate.propTypes = {
  data: PropTypes.any,
  minHrBPM: PropTypes.number,
  maxHrBPM: PropTypes.number,
  hideSettings: PropTypes.bool,
  compact: PropTypes.bool,
};
